import React from "react";

// The portal's one on/off switch, as pixels: the track's fill, the X and the
// checkmark, and the dot that slides between them. Two controls draw a switch —
// the rjsf boolean widget (CustomCheckbox, a headless-ui Switch) and the action
// sections' ScheduleToggle (a plain button with role="switch") — and they had
// drifted into two different-looking things: different sizes, different greys,
// and only one of them carrying the icons.
//
// DESIGN.md has no switch component: it defines colour tokens, typography and
// icons, and nothing about toggles. So the shape and the icons are this app's
// own convention, kept in one place here rather than settled twice. What DESIGN.md
// DOES settle is the colours, and those are named below.
//
// Geometry lives in routeDetailsClasses.switchContainer (56x28, 20px dot).

// On is UI/CTA Primary: "used for calls to action, links, and active states"
// (DESIGN.md). Brand/Accent Green shares its value — same colour, and the CTA
// name is the one that describes an interactive control.
//
// Off is a greyscale step rather than UI/Status/Disabled. The two resolve to the
// same grey, but DESIGN.md is explicit that status colours are "strictly
// functional: use them only to communicate system state... never decoratively",
// and a switch's track is chrome rather than a status report.
export const switchTrackFill = (on: boolean) =>
    on
        ? "bg-ui-cta-primary focus:ring-ui-cta-primary/50"
        : "bg-greyscale-6 focus:ring-greyscale-6/50";

// Everything inside the track. The parent supplies the track itself, because one
// caller needs a headless-ui <Switch> and the other a <span> inside its button.
const SwitchVisual = ({ on }: { on: boolean }) => (
    <>
        {/* X on the right while off, checkmark on the left while on: both are
            always mounted and cross-fade, so neither the track nor the dot shifts
            as the state changes. */}
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`absolute right-2 top-1.5 h-4 w-4 text-white transition-opacity duration-200 ${on ? "opacity-0" : "opacity-100"}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
        </svg>
        <svg
            xmlns="http://www.w3.org/2000/svg"
            className={`absolute left-2 top-1.5 h-4 w-4 text-white transition-opacity duration-200 ${on ? "opacity-100" : "opacity-0"}`}
            fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true"
        >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
        </svg>
        <span
            aria-hidden="true"
            className={`pointer-events-none inline-block size-5 rounded-full bg-white ring-0 shadow-lg transition-transform duration-200 ease-in-out ${on ? "translate-x-7" : "translate-x-0"}`}
        />
    </>
);

export default SwitchVisual;
